import useEcho from "@/hooks/use-echo.hook.ts";
import { Angle } from "@/icons/Angle.tsx";
import { Aperture } from "@/icons/Aperture.tsx";
import { Brightness } from "@/icons/Brightness.tsx";
import { CircleCheck } from "@/icons/CircleCheck.tsx";
import { HeartHalf } from "@/icons/HeartHalf.tsx";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import { MicrochipAI } from "@/icons/MicrochipAI.tsx";
import { Palette } from "@/icons/Palette.tsx";
import { PersonRays } from "@/icons/PersonRays.tsx";
import { WandMagicSparkles } from "@/icons/WandMagicSparkles.tsx";
import api from "@/lib/api.ts";
import {
  accessoryReducer,
  AspectRatio,
  getPhotoDownloadUrl,
  initialAccessoriesState,
  Outputs,
  photoAngleMap,
  photoAnglePhotoMap,
  photoBackgroundMap,
  photoBackgroundPhotoMap,
  photoExpressionMap,
  photoExpressionPhotoMap,
  photoFocusMap,
  photoFocusPhotoMap,
  photoLightingMap,
  photoLightingPhotoMap,
  photoStyleMap,
  photoStylePhotoMap,
} from "@/lib/helpers.ts";
import { LoraModel } from "@/models/lora.model.ts";
import {
  PhotoModel,
  PhotoResponse,
  PhotosResponse,
} from "@/models/photo.model.ts";
import { getPhotos } from "@/services/photo.service.ts";
import useUserStore from "@/store/user.store.ts";
import { AccessoriesSelect } from "@/ui/shared/AccessoriesSelect.tsx";
import { DesktopView } from "@/ui/shared/DesktopView.tsx";
import { HairSelect } from "@/ui/shared/HairSelect.tsx";
import { LoraFilter } from "@/ui/shared/LoraFilter.tsx";
import { LoraSelect } from "@/ui/shared/LoraSelect.tsx";
import { MobileView } from "@/ui/shared/MobileView.tsx";
import { OptionSelect } from "@/ui/shared/OptionSelect.tsx";
import { PhotoGrid } from "@/ui/shared/PhotoGrid.tsx";
import { PhotoLightBox } from "@/ui/shared/PhotoLightBox.tsx";
import {
  Button,
  Card,
  CloseButton,
  FileButton,
  Image,
  SegmentedControl,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  useDebouncedValue,
  useHotkeys,
  useLocalStorage,
  useMediaQuery,
  useScrollIntoView,
} from "@mantine/hooks";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  useEffect,
  useState,
  useCallback,
  useReducer,
  useMemo,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { AspectRatioSelect } from "@/ui/shared/AspectRatioSelect";

type Filter = "all" | "starred" | "liked";

export function Playground() {
  const matches = useMediaQuery("(min-width: 56.25em)");
  const limit = 20;
  const echo = useEcho();
  const { webSocketsUserId } = useUserStore();
  const { user } = useUserStore();

  useEffect(() => {
    if (echo && webSocketsUserId) {
      echo
        .private(`playground.${webSocketsUserId}`)
        .listen("PhotosUpdated", () => {
          refetch();
        });
    }

    return () => {
      if (echo && webSocketsUserId) {
        echo
          .private(`playground.${webSocketsUserId}`)
          .stopListening("PhotosUpdated");
      }
    };
  }, [echo, webSocketsUserId]);

  const form = useForm<{
    prompt: string;
  }>({
    defaultValues: {
      prompt: "",
    },
  });

  const resetRef = useRef<() => void>(null);
  const { ref, inView } = useInView();
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 100,
  });
  const [file, setFile] = useState<File | null>(null);
  const [inputPhoto, setInputPhoto] = useState<PhotoModel | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [lora, setLora] = useState<LoraModel>();
  const [loraFilter, setLoraFilter] = useState<LoraModel>();
  const [style, setStyle] = useState<string | "">("realistic");
  const [focus, setFocus] = useState<string | "">("");
  const [expression, setExpression] = useState<string | "">("");
  const [accessories, setAccessories] = useReducer(
    accessoryReducer,
    initialAccessoriesState,
  );
  const [angle, setAngle] = useState<string | "">("");
  const [background, setBackground] = useState<string | "">("");
  const [hair, setHair] = useState<string | "">("");
  const [lighting, setLighting] = useState<string | "">("natural");
  const [outputs, setOutputs] = useState<Outputs>("4");
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [aspectRatio, setAspectRatio] = useLocalStorage<AspectRatio>({
    key: "aspect-ratio",
    defaultValue: "1:1",
  });
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [viewerOpened, { open: openViewer, close: closeViewer }] =
    useDisclosure(false);
  const [enhance, setEnhance] = useState<boolean>(false);

  const predictionMutation = useMutation<AxiosResponse<PhotosResponse>>({
    mutationFn: () => {
      return api.post("/playground", {
        app: "playground",
        prompt: form.getValues("prompt"),
        enhance,
        aspect_ratio: aspectRatio,
        style,
        focus,
        lighting,
        expression,
        background,
        angle,
        accessories,
        hair,
        outputs,
        lora_id: lora?.id,
        input_photo_id: inputPhoto?.id,
      });
    },
  });

  const uploadMutation = useMutation<AxiosResponse<PhotoResponse>>({
    mutationFn: () => {
      return api.post(
        "/upload",
        {
          app: "mirror",
          file,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },
  });

  useEffect(() => {
    if (uploadMutation.data?.data) {
      setInputPhoto(uploadMutation.data.data.data);
    }
  }, [uploadMutation.data]);

  const handleCaptionMutation = () => {
    if (inputPhoto && inputPhoto.caption) {
      form.setValue("prompt", inputPhoto.caption);
      return;
    }
    captionMutation.mutate();
  };

  const userHasCredits: boolean = user ? user.credits > 0 : false;

  const captionMutation = useMutation<AxiosResponse<{ prompt: string }>>({
    mutationFn: () => {
      return api.post("/prompts/caption", {
        input_photo_id: inputPhoto?.id,
      });
    },
  });

  useEffect(() => {
    if (captionMutation.data?.data.prompt) {
      form.setValue("prompt", captionMutation.data.data.prompt);
    }
  }, [captionMutation.data]);

  useEffect(() => {
    if (file) {
      uploadMutation.mutate();
    }
  }, [file]);

  useEffect(() => {
    if (lora?.id !== loraFilter?.id) {
      setLoraFilter(undefined);
    }
  }, [lora]);

  const handleGenerate = () => {
    predictionMutation.mutate();
    scrollIntoView({
      alignment: "start",
    });
    setSearch("");
    setFilter("all");
  };

  const { data, isFetching, fetchNextPage, hasNextPage, refetch, isError } =
    useInfiniteQuery({
      retryOnMount: false,
      retry: 1,
      queryKey: [
        "photos",
        {
          limit,
          sort: "-created_at",
          "filter[filter]": filter,
          "filter[search]": debouncedSearch,
          "filter[app]": "playground",
          "filter[lora_id]": loraFilter?.id,
          include: "lora",
        },
      ],
      queryFn: (settings) => {
        return getPhotos({
          limit,
          sort: "-created_at",
          page: settings.pageParam,
          "filter[type]": "output",
          "filter[filter]": filter,
          "filter[search]": debouncedSearch,
          "filter[app]": "playground",
          "filter[lora_id]": loraFilter?.id,
          include: "lora",
        });
      },
      initialPageParam: 1,
      getPreviousPageParam: (response) => {
        const meta = response.meta;

        if (meta.current_page === 1) {
          return null;
        }
        return meta.current_page - 1;
      },
      getNextPageParam: (response) => {
        const meta = response.meta;
        if (meta.current_page === meta.last_page) {
          return null;
        }
        return meta.current_page + 1;
      },
    });

  useEffect(() => {
    setTotalPhotos(data?.pages[0]?.meta.total ?? 0);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, hasNextPage]);

  const handlePhotoClick = useCallback(
    (pageIndex: number, photoIndex: number) => {
      openViewer();
      const index = pageIndex * limit + photoIndex;
      setActivePhotoIndex(index);
    },
    [openViewer, setActivePhotoIndex],
  );

  useHotkeys([["enter", handleGenerate]], ["TEXTAREA"]);

  const memoizedAccessories = useMemo(() => accessories, [accessories]);

  const memoizedData = useMemo(() => data, [data]);

  const slides = useMemo(() => {
    return (
      data?.pages
        .map((page) => page.data.map((photo) => photo))
        .flat(1)
        .map((photo) => ({
          src: photo.url,
          downloadUrl: getPhotoDownloadUrl(photo),
          description: photo.prompt,
        })) || []
    );
  }, [data]);

  const lightBox = memoizedData && (
    <PhotoLightBox
      viewerOpened={viewerOpened}
      activePhotoIndex={activePhotoIndex}
      closeViewer={closeViewer}
      slides={slides}
    />
  );

  const sideBar = (
    <div className="flex flex-col gap-2">
      <Card padding="lg" withBorder>
        <Card.Section withBorder inheritPadding py="md">
          <div className="flex flex-col gap-2">
            <LoraSelect lora={lora} setLora={setLora} />
            <OptionSelect
              label="Style"
              value={style}
              setValue={setStyle}
              map={photoStyleMap}
              photoMap={photoStylePhotoMap}
              icon={<Palette />}
            />
            <OptionSelect
              label="Expression"
              value={expression}
              setValue={setExpression}
              map={photoExpressionMap}
              photoMap={photoExpressionPhotoMap}
              icon={<HeartHalf />}
            />
            <OptionSelect
              label="Angle"
              value={angle}
              setValue={setAngle}
              map={photoAngleMap}
              photoMap={photoAnglePhotoMap}
              icon={<Angle />}
            />
            <OptionSelect
              label="Focus"
              value={focus}
              setValue={setFocus}
              map={photoFocusMap}
              photoMap={photoFocusPhotoMap}
              icon={<Aperture />}
            />
            <OptionSelect
              label="Lighting"
              value={lighting}
              setValue={setLighting}
              map={photoLightingMap}
              photoMap={photoLightingPhotoMap}
              icon={<Brightness />}
            />
            <OptionSelect
              label="Background"
              value={background}
              setValue={setBackground}
              map={photoBackgroundMap}
              photoMap={photoBackgroundPhotoMap}
              icon={<PersonRays />}
            />
            <HairSelect value={hair} setValue={setHair} lora={lora} />
            <AccessoriesSelect
              accessories={memoizedAccessories}
              setAccessories={setAccessories}
            />
            <AspectRatioSelect value={aspectRatio} setValue={setAspectRatio} />

            <div className="flex flex-col gap-0.5">
              <Text fw={500} size="md" component="label" mb={0}>
                Number of variations
              </Text>

              <SegmentedControl
                color="violet"
                value={outputs}
                onChange={(value) => setOutputs(value as Outputs)}
                data={[
                  {
                    label: "1",
                    value: "1",
                  },
                  {
                    label: "2",
                    value: "2",
                  },
                  {
                    label: "3",
                    value: "3",
                  },
                  {
                    label: "4",
                    value: "4",
                  },
                ]}
              />
            </div>

            <div className="flex flex-col gap-0.5">
              <Text fw={500} size="md" component="label" mb={0}>
                Input photo
              </Text>

              <div className="flex gap-2">
                <FileButton
                  disabled={!userHasCredits}
                  resetRef={resetRef}
                  onChange={(e) => {
                    setFile(e);
                    setInputPhoto(null);
                  }}
                  accept={IMAGE_MIME_TYPE.join(",")}
                >
                  {(props) => (
                    <Button fullWidth={true} variant="default" {...props}>
                      Add photo
                    </Button>
                  )}
                </FileButton>

                {(file || inputPhoto) && (
                  <Button
                    onClick={() => {
                      setFile(null);
                      setInputPhoto(null);
                    }}
                    fullWidth={true}
                    variant="default"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {(file || inputPhoto) && (
                <div className="flex flex-col relative">
                  {inputPhoto && !file && (
                    <Image
                      mt={5}
                      radius="md"
                      src={inputPhoto.url}
                      alt="Image source"
                    />
                  )}

                  {file && (
                    <Image
                      mt={5}
                      radius="md"
                      src={URL.createObjectURL(file)}
                      alt="Image source"
                    />
                  )}

                  <div className="absolute bottom-0 p-2 w-full flex gap-2">
                    {inputPhoto && (
                      <Button
                        size="xs"
                        fullWidth={true}
                        variant="default"
                        disabled={captionMutation.isPending}
                        loading={captionMutation.isPending}
                        onClick={handleCaptionMutation}
                      >
                        Generate prompt
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Textarea
              {...form.register("prompt")}
              size="md"
              label="Prompt"
              placeholder="Explain what you want to see..."
              autosize
              minRows={5}
              maxRows={10}
            />

            <div className="grid grid-cols-2 items-center gap-2">
              <Button
                onClick={() => form.setValue("prompt", "")}
                variant="default"
                fullWidth
              >
                Clear
              </Button>

              <Tooltip
                withArrow={true}
                position="bottom"
                label="Type a simple prompt and let AI enhance it for more artistic results"
              >
                <Button
                  c={enhance ? "green" : ""}
                  onClick={() => setEnhance(!enhance)}
                  variant="default"
                  leftSection={
                    enhance ? (
                      <CircleCheck size={14} />
                    ) : (
                      <WandMagicSparkles size={14} />
                    )
                  }
                  fullWidth
                >
                  Enhance
                </Button>
              </Tooltip>
            </div>
          </div>
        </Card.Section>

        <Card.Section withBorder inheritPadding py="md">
          <Tooltip
            withArrow={true}
            position="bottom"
            label={`${+outputs * 10} credits`}
          >
            <Button
              disabled={predictionMutation.isPending}
              loading={predictionMutation.isPending}
              onClick={() => {
                if (form.getValues("prompt")?.length < 5 && !lora) {
                  notifications.show({
                    title: "Warning!",
                    message:
                      "You need to either select a character or add a prompt before generating some images.",
                    color: "orange",
                  });
                  return;
                }

                handleGenerate();
              }}
              variant="gradient"
              gradient={{ from: "grape", to: "violet", deg: 90 }}
              leftSection={<MicrochipAI />}
              fullWidth
            >
              Generate
            </Button>
          </Tooltip>
        </Card.Section>
      </Card>

      <Text c="dimmed" size="sm" ta="center">
        AI may make mistakes. Please use with discretion.
      </Text>
    </div>
  );

  const mainContent = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2 md:flex-row w-full justify-start md:justify-between md:items-center">
        <Title order={3} fw={500}>
          Photos ({totalPhotos || 0})
        </Title>

        <TextInput
          miw={300}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          leftSection={<MagnifyingGlass size={14} />}
          placeholder="Search photos by prompt..."
          rightSection={
            search && (
              <CloseButton
                className="cursor-pointer"
                aria-label="Clear input"
                onClick={() => setSearch("")}
              />
            )
          }
        />

        <div className="flex items-center gap-2">
          <SegmentedControl
            color="grape"
            value={filter}
            onChange={(value) => setFilter(value as Filter)}
            data={[
              {
                label: "All",
                value: "all",
              },
              {
                label: "Starred",
                value: "starred",
              },
              {
                label: "Liked",
                value: "liked",
              },
            ]}
          />

          <LoraFilter lora={loraFilter} setLora={setLoraFilter} />
        </div>
      </div>

      <PhotoGrid
        onPhotoCopySettings={(photo: PhotoModel) => {
          setAngle(photo.angle);
          setExpression(photo.expression);
          setFocus(photo.focus);
          setLighting(photo.lighting);
          setBackground(photo.background);
          setStyle(photo.style);
          form.setValue("prompt", photo.prompt);
        }}
        data={memoizedData}
        form={form}
        handlePhotoClick={handlePhotoClick}
        isFetching={isFetching}
        isError={isError}
        inViewRef={ref}
      />
    </div>
  );

  return (
    <>
      <div ref={targetRef} className="flex flex-col gap-5">
        {matches ? (
          <DesktopView sideBar={sideBar} mainContent={mainContent} />
        ) : (
          <MobileView
            generating={predictionMutation.isPending}
            sideBar={sideBar}
            mainContent={mainContent}
          />
        )}
      </div>

      {lightBox}
    </>
  );
}
